import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { CheckUserAuth, getDeveloper, getPublisher } from '../Api/WebApi';
import { getPackageNameNoPrefix } from '../Models/AugerOffers/MarketplaceDetails';
import { useRomaWebApi } from '../Api/RomaWebApi';
import version from '../version.json';
import { Permission, UserRole, getRolePermissions } from "../Models/User";
import { UserFeature, parseFeatures, defaultFeatures } from "../Models/UserFeature";
import { Creator } from "../Models/Creator";
import { loginScopes, requiresInteraction, signalMsalReady, msalReadyPromise } from "./msalApp";
import { debugLog } from "../Utils/DebugOverlay";

export interface AuthorizationState {
  isLoading: boolean;
  isAuthorized: boolean;
  isInternal: boolean;
  isExternal: boolean;
  isAdministrator: boolean;
  userName: string;
  userEmail: string;
  publishers: Creator[];
  developers: Creator[];
  features: UserFeature;
  lastVersion: string | null;
  role: string | null;
  creators: Record<string, string>;
  aneidaApprovedPackages: string[];
  login: (forcePrompt?: boolean) => Promise<boolean>;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  isSignedInWithMSA: () => boolean;
  refreshAuthorization: () => Promise<void>;
  hasPermission: (permission: Permission, publisherId?: string, developerId?: string) => boolean;
  isPackageAneidaApproved: (packageName?: string) => boolean;
  refreshApprovedPackages: () => Promise<void>;
}

const defaultState: AuthorizationState = {
  isLoading: true,
  isAuthorized: false,
  isInternal: false,
  isExternal: true,
  isAdministrator: false,
  userName: "",
  userEmail: "",
  publishers: [],
  developers: [],
  features: defaultFeatures,
  lastVersion: null,
  role: null,
  creators: {},
  aneidaApprovedPackages: [],
  login: async () => false,
  logout: () => {},
  getAccessToken: async () => { throw new Error("AuthorizationProvider not initialized"); },
  isSignedInWithMSA: () => false,
  refreshAuthorization: async () => {},
  hasPermission: () => false,
  isPackageAneidaApproved: () => false,
  refreshApprovedPackages: async () => {}
};

function deriveRoleFlags(role: string | null) {
  const isAdministrator = role === UserRole.Administrator;
  const isInternal = isAdministrator || role === UserRole.ContentManager || role === UserRole.Support;
  return { isAdministrator, isInternal, isExternal: !isInternal };
}

function getInitialState(): AuthorizationState {
  const tabAccountId = sessionStorage.getItem('tabAccountId');
  if (tabAccountId) {
    const role = sessionStorage.getItem('role');
    const userEmail = sessionStorage.getItem('userEmail') || '';
    return {
      ...defaultState,
      isLoading: true,
      isAuthorized: true,
      role: role,
      userEmail: userEmail
    };
  }
  // Fresh browser session. MSAL v4 purges its encrypted localStorage cache whenever the
  // session cookie holding the encryption key is lost (OS reclaiming the browser/PWA
  // process), so a returning user looks signed out despite a durable hint. Attempt silent
  // recovery once per session; enableSSO is not consulted because it governs the account
  // picker at explicit login, not recovery from cache loss.
  if (localStorage.getItem('loginHint') && !sessionStorage.getItem('ssoAttempted')) {
    sessionStorage.setItem('ssoAttempted', 'true');
    return {
      ...defaultState,
      isLoading: true,
      isAuthorized: true,
      userEmail: localStorage.getItem('loginHint') || ''
    };
  }
  return defaultState;
}

const AuthorizationContext = createContext<AuthorizationState>(defaultState);

export function AuthorizationProvider({ children }: { children: React.ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [state, setState] = useState<AuthorizationState>(getInitialState);
  const isRefreshingRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);

  const acquireToken = useCallback(async (): Promise<string> => {
    const tabAccountId = sessionStorage.getItem('tabAccountId');
    const accounts = instance.getAllAccounts();

    let account = tabAccountId
      ? accounts.find(a => a.homeAccountId === tabAccountId)
      : accounts[0];

    if (!account && accounts.length > 0) {
      account = accounts[0];
    }

    if (!account) {
      throw new Error("No accounts available");
    }

    if (account.homeAccountId !== tabAccountId) {
      sessionStorage.setItem('tabAccountId', account.homeAccountId);
    }

    const authenticationResult = await instance.acquireTokenSilent({ account, scopes: loginScopes }).catch(async error => {
      // Distinguishes a genuine re-auth requirement (interaction_required) from a
      // blocked/slow hidden-iframe renewal (monitor_window_timeout), which is the
      // normal failure on iOS Safari and in PWAs.
      debugLog('acquireToken:silentFail', () => ({
        errorCode: error?.errorCode ?? '(none)',
        needsInteraction: requiresInteraction(error?.errorCode),
        accountCount: instance.getAllAccounts().length,
      }));
      if (requiresInteraction(error.errorCode)) {
        // Full-page redirect rather than a popup: this can run with no user gesture
        // (page init, a background SignalR reconnect, a timer-driven fetch), and a
        // popup in that situation is blocked by browsers — mobile especially.
        // The document navigates away, so no token is returned to this caller.
        await instance.acquireTokenRedirect({ account, scopes: loginScopes });
        throw error;
      }
      console.error('Non-interactive error:', error);
      throw error;
    });

    return authenticationResult.accessToken;
  }, [instance]);

  const authOverride = useMemo(() => ({ getAccessToken: acquireToken }) as any, [acquireToken]);
  const api = useRomaWebApi(authOverride);

  const refreshAuthorization = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      setState(prev => ({ ...prev, isLoading: true }));

      const accessToken = await acquireToken();
      const result = await CheckUserAuth(accessToken);

      if (result) {
        const authObj = { getAccessToken: acquireToken } as any;

        const publishers = await getPublisher(authObj);
        const developers = await getDeveloper(authObj);

        let aneidaApprovedPackages: string[] = [];
        try {
          const settings = await api.getAdminSettings();
          aneidaApprovedPackages = settings.aneidaApprovedPackages ?? [];
        } catch (e) {
          console.error("Failed to load PlayStation approved packages:", e);
        }

        const role = result.role;

        const creators: Record<string, string> = result.creators ?? {};

        const features: UserFeature = result.features ?? defaultFeatures;

        sessionStorage.setItem('role', role);
        sessionStorage.setItem('userEmail', result.email || '');
        localStorage.setItem('loginHint', result.email || '');

        setState({
          ...defaultState,
          isLoading: false,
          isAuthorized: true,
          userName: result.name,
          userEmail: result.email || '',
          publishers,
          developers,
          features,
          lastVersion: version.version,
          role: role,
          creators: creators,
          aneidaApprovedPackages,
        });
      } else {
        debugLog('refreshAuth:logout', { reason: 'CheckUserAuth returned falsy' });
        setState({ ...defaultState, isLoading: false });
      }
    } catch (error: any) {
      console.error("Authorization check failed:", error);
      // Every visible logout that isn't user-initiated passes through here.
      debugLog('refreshAuth:logout', () => ({
        reason: 'threw',
        status: error?.response?.status ?? '(none)',
        errorCode: error?.errorCode ?? '(none)',
        name: error?.name ?? '(none)',
        message: String(error?.message ?? error).slice(0, 120),
      }));
      setState({ ...defaultState, isLoading: false });
    } finally {
      isRefreshingRef.current = false;
    }
  }, [acquireToken, api]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    signalMsalReady();

    if (!isMsalAuthenticated) {
      if (state.isAuthorized) {
        // MSAL cache was lost (encryption key is a session cookie cleared on browser close).
        // Try silent login to re-establish the session without user interaction.
        const loginRequest = {
          scopes: loginScopes,
          prompt: 'none' as const,
          loginHint: state.userEmail || undefined
        };

        debugLog('init:recovery', () => ({ hint: state.userEmail || '(none)' }));

        instance.loginRedirect(loginRequest).catch((err: any) => {
          debugLog('init:recoveryFail', () => ({ errorCode: err?.errorCode ?? '(none)' }));
          setState({ ...defaultState, isLoading: false });
        });
      } else {
        // No recovery is attempted from here; the user sees a signed-out app.
        debugLog('init:noRecovery', () => ({
          accountCount: instance.getAllAccounts().length,
          hasTabPin: !!sessionStorage.getItem('tabAccountId'),
          hasLoginHint: !!localStorage.getItem('loginHint'),
        }));
      }
      return;
    }

    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    refreshAuthorization();
  }, [isMsalAuthenticated, inProgress, refreshAuthorization]);

  const hasPermission = useCallback((permission: Permission, publisherId?: string, developerId?: string): boolean => {
    // Effective permission = role permissions OR (creatorMatch permissions AND per-creator cap)
    let effectivePermissions = getRolePermissions(state.role ?? '');

    const publisherPerms = getRolePermissions(UserRole.Publisher);
    const developerPerms = getRolePermissions(UserRole.Developer);

    if (publisherId && state.creators[publisherId] !== undefined) {
      const publisherIdMatchRole = getRolePermissions(state.creators[publisherId]);
      if ((publisherIdMatchRole & publisherPerms) === publisherPerms) {
        effectivePermissions |= publisherPerms;
      }
    }

    if (developerId && state.creators[developerId] !== undefined) {
      const developerIdMatchRole = getRolePermissions(state.creators[developerId]);
      if ((developerIdMatchRole & developerPerms) === developerPerms) {
        effectivePermissions |= developerPerms;
      }
    }

    return (effectivePermissions & permission) === permission;
  }, [state.creators, state.role]);

  const isPackageAneidaApproved = useCallback((packageName?: string): boolean => {
    if (deriveRoleFlags(state.role).isInternal) return true;
    if (!packageName || !state.features.aneida) return false;
    return state.aneidaApprovedPackages.some(p => p.toLowerCase() === getPackageNameNoPrefix(packageName)!.toLowerCase());
  }, [state.role, state.features, state.aneidaApprovedPackages]);

  const refreshApprovedPackages = useCallback(async () => {
    try {
      const settings = await api.getAdminSettings();
      setState(prev => ({ ...prev, aneidaApprovedPackages: settings.aneidaApprovedPackages ?? [] }));
    } catch (e) {
      console.error("Failed to refresh PlayStation approved packages:", e);
    }
  }, [api]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    await msalReadyPromise;
    return acquireToken();
  }, [acquireToken]);

  const login = useCallback(async (forcePrompt?: boolean) => {
    console.log("login() called.");

    sessionStorage.removeItem('role');
    sessionStorage.removeItem('userEmail');
    localStorage.removeItem("showBanner");
    localStorage.removeItem("hideBannerVersion");

    console.log("onSignIn() called.");

    let prompt: 'login' | 'select_account' | undefined;
    if (forcePrompt) {
      prompt = 'select_account';
      instance.setActiveAccount(null);
    } else {
      prompt = localStorage.getItem('enableSSO') === 'true' ? undefined : 'select_account';
    }

    await instance.loginRedirect({
      scopes: loginScopes,
      prompt
    });
    return true;
  }, [instance]);

  const logout = useCallback(() => {
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('userEmail');
    localStorage.removeItem("showBanner");
    localStorage.removeItem("hideBannerVersion");
    localStorage.removeItem("loginHint");

    const tabAccountId = sessionStorage.getItem('tabAccountId');
    const accountToLogout = tabAccountId
      ? instance.getAllAccounts().find(a => a.homeAccountId === tabAccountId)
      : undefined;
    sessionStorage.removeItem('tabAccountId');

    instance.logoutRedirect(accountToLogout ? { account: accountToLogout } : undefined);
  }, [instance]);

  const isSignedInWithMSA = useCallback(() => {
    try {
      const tabAccountId = sessionStorage.getItem('tabAccountId');
      if (!tabAccountId) return false;
      return instance.getAllAccounts().some(a => a.homeAccountId === tabAccountId);
    } catch {
      return false;
    }
  }, [instance]);

  const contextValue = useMemo(() => ({
    ...state,
    ...deriveRoleFlags(state.role),
    login,
    logout,
    getAccessToken,
    isSignedInWithMSA,
    refreshAuthorization,
    hasPermission,
    isPackageAneidaApproved,
    refreshApprovedPackages,
  }), [state, login, logout, getAccessToken, isSignedInWithMSA, refreshAuthorization, hasPermission, isPackageAneidaApproved, refreshApprovedPackages]);

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
}

export function useAuthorization(): AuthorizationState {
  return useContext(AuthorizationContext);
}
